import React, { useCallback, memo } from 'react';
import Particles from 'react-particles';
import { loadFull } from 'tsparticles';
import theme from '../styles/theme';

/**
 * ParticleBackground Component
 * Creates an animated particle background effect for the gaming/esports aesthetic
 * Optimized for better performance with reduced particle count and simplified effects
 *
 * @param {Object} props - Component props
 * @param {boolean} props.celebration - Whether to show celebration particles
 */
const ParticleBackground = ({ celebration = false }) => {
  // Initialize particles engine
  const particlesInit = useCallback(async (engine) => {
    await loadFull(engine);
  }, []);

  // Standard background particle configuration - optimized for performance
  const backgroundConfig = {
    fullScreen: {
      enable: true,
      zIndex: -1
    },
    fpsLimit: 30, // Reduced from 60 to 30 for better performance
    particles: {
      number: {
        value: 40, // Reduced from 80 to 40 for better performance
        density: {
          enable: true,
          value_area: 800
        }
      },
      color: {
        value: [theme.colors.primary, theme.colors.secondary]
      },
      shape: {
        type: "circle", // Simplified to just circles
        stroke: {
          width: 0,
          color: "#000000"
        }
      },
      opacity: {
        value: 0.3,
        random: true,
        anim: {
          enable: false, // Disabled animation for better performance
          speed: 0.5,
          opacity_min: 0.1,
          sync: false
        }
      },
      size: {
        value: 3,
        random: true,
        anim: {
          enable: false, // Disabled animation for better performance
          speed: 2,
          size_min: 0.1,
          sync: false
        }
      },
      line_linked: {
        enable: true,
        distance: 150,
        color: theme.colors.primary,
        opacity: 0.2,
        width: 1
      },
      move: {
        enable: true,
        speed: 0.8, // Reduced from 1 to 0.8 for better performance
        direction: "none",
        random: true,
        straight: false,
        out_mode: "out",
        bounce: false,
        attract: {
          enable: false, // Disabled for better performance
          rotateX: 600,
          rotateY: 1200
        }
      }
    },
    interactivity: {
      detect_on: "canvas",
      events: {
        onhover: {
          enable: false, // Disabled for better performance
          mode: "grab"
        },
        onclick: {
          enable: true,
          mode: "push"
        },
        resize: true
      },
      modes: {
        push: {
          particles_nb: 2 // Reduced from 4 to 2
        }
      }
    },
    retina_detect: false // Disabled for better performance
  };

  // Celebration particle configuration - optimized for performance
  const celebrationConfig = {
    fullScreen: {
      enable: true,
      zIndex: 1
    },
    fpsLimit: 30, // Reduced from 60 to 30
    particles: {
      number: {
        value: 60, // Reduced from 150 to 60
        density: {
          enable: true,
          value_area: 800
        }
      },
      color: {
        value: [
          theme.colors.gold,
          theme.colors.primary,
          theme.colors.secondary
        ] // Reduced color variety
      },
      shape: {
        type: ["circle", "star"], // Simplified shapes
        stroke: {
          width: 0,
          color: "#000000"
        }
      },
      opacity: {
        value: 0.7,
        random: true,
        anim: {
          enable: false,
          speed: 1,
          opacity_min: 0.1,
          sync: false
        }
      },
      size: {
        value: 5,
        random: true,
        anim: {
          enable: false,
          speed: 40,
          size_min: 0.1,
          sync: false
        }
      },
      line_linked: {
        enable: false
      },
      move: {
        enable: true,
        speed: 3, // Reduced from 5 to 3
        direction: "top",
        random: true,
        straight: false,
        out_mode: "out",
        bounce: false,
        attract: {
          enable: false,
          rotateX: 600,
          rotateY: 1200
        }
      }
    },
    interactivity: {
      detect_on: "canvas",
      events: {
        onhover: {
          enable: false, // Disabled for better performance
          mode: "repulse"
        },
        onclick: {
          enable: true,
          mode: "push"
        },
        resize: true
      },
      modes: {
        push: {
          particles_nb: 2 // Reduced from 4 to 2
        }
      }
    },
    retina_detect: false // Disabled for better performance
  };

  return (
    <Particles
      id="tsparticles"
      init={particlesInit}
      options={celebration ? celebrationConfig : backgroundConfig}
    />
  );
};

// Memoize the component to prevent unnecessary re-renders
export default memo(ParticleBackground);